import moment from 'moment';
import { STATUS } from 'src/constants/common';

/**
 * Mirrors the expired-rate logic from the internal ActiveQuotesRow reference.
 * The customer portal API does not return carrierQuoteId, so we rely on
 * isPrivateCarrier + expirationDate directly (both present in the response).
 * Shared by SelectQuoteTableRow (visual indicator) and SelectQuoteBlock
 * (blocks booking an expired rate) so the two can't drift out of sync.
 */
export const isExpiredQuoteRate = (data: any, userTimezone: string): boolean => {
    if (!data || data.isPrivateCarrier || !data.expirationDate || data.status === STATUS.WON) return false;

    const currentDateString = moment().tz(userTimezone).format('YYYY-MM-DD');
    const expirationDateString = moment.utc(data.expirationDate).startOf('day').format('YYYY-MM-DD');

    return moment(expirationDateString).isBefore(moment(currentDateString), 'day');
};

const getDimensions = (dimensions: any[]) =>
    dimensions?.map((dimension) => ({
        id: dimension?.id,
        handlingUnit: dimension?.handlingUnit,
        handlingUnitNo: dimension?.handlingUnitNo,
        weight: dimension?.weight,
        width: dimension?.width,
        height: dimension?.height,
        totalWeight: dimension?.totalWeight,
        isStack: dimension?.isStack,
        weightMeasurement: dimension?.weightMeasurement,
        lengthMeasurement: dimension?.lengthMeasurement,
        commodity: dimension?.commodity,
        freightClass: dimension?.freightClass,
        itemClassForDayross: dimension?.itemClassForDayross,
        unitWeight: dimension?.unitWeight || 0,
        length: dimension?.itemLength || 0,
        // Client decision — the Book Quote stepper's Load Details step shows a read-only "Picks up
        // at"/"Delivers at" per line item. Already resolved to positions by the backend (see
        // getQuoteById.controller.ts) — null on classic (non-multi-stop) quotes.
        pickupStopPosition: dimension?.pickupStopPosition ?? null,
        dropStopPosition: dimension?.dropStopPosition ?? null,
    })) || [];

export const getQuoteDimensions = (quote: any) => ({
    weightMeasurement: quote?.dimensions?.[0]?.weightMeasurement || 'LBS',
    lengthMeasurement: quote?.dimensions?.[0]?.lengthMeasurement || 'INCHES',
    totalWeight: quote?.totalWeight || null,
    linearFootage: quote?.linearFootage ? parseFloat(quote?.linearFootage) : null,
    dimensions: quote?.dimensions ? getDimensions(quote?.dimensions) : [],
    serviceId: quote?.selectedServices?.map((item: any) => item.serviceId) || [],
    servicesData: quote?.selectedServices || [],
});

export const addActiveQuoteTag = (carriers: any[]) => {
    if (!carriers?.length) return carriers;

    const publicCarriers = carriers;

    const publicCarriersSortedByPrice = [...publicCarriers].sort((a, b) => {
        const chargeA = a?.finalChargeUSD ?? a?.finalCharge ?? 0;
        const chargeB = b?.finalChargeUSD ?? b?.finalCharge ?? 0;

        return chargeA - chargeB;
    });

    const cheapestCarriers = publicCarriersSortedByPrice
        .filter((carrier) => (publicCarriersSortedByPrice[0]?.finalChargeUSD ?? publicCarriersSortedByPrice[0]?.finalCharge) === (carrier?.finalChargeUSD ?? carrier?.finalCharge))
        .sort((a, b) => {
            const timeA = a.transitTime ?? 0;
            const timeB = b.transitTime ?? 0;

            return timeA - timeB;
        });

    const cheapestCarrierIds: number[] = [];

    cheapestCarriers.forEach((carrier: any) => {
        if (cheapestCarriers[0]?.transitTime === carrier?.transitTime && cheapestCarrierIds.length === 0) {
            cheapestCarrierIds.push(carrier.id);
        }
    });

    const fastestCarriers = publicCarriersSortedByPrice
        .filter((carrier) => !cheapestCarrierIds.includes(carrier.id))
        .sort((a, b) => {
            const timeA = a.transitTime ?? 0;
            const timeB = b.transitTime ?? 0;

            return timeA - timeB;
        });

    const expressCarrierIds: number[] = [];

    fastestCarriers.forEach((carrier: any) => {
        if ((fastestCarriers[0]?.finalChargeUSD ?? fastestCarriers[0]?.finalCharge) === (carrier?.finalChargeUSD ?? carrier?.finalCharge) && expressCarrierIds.length === 0) {
            expressCarrierIds.push(carrier.id);
        }
    });

    return carriers.map((carrier) => ({
        ...carrier,
        tag: cheapestCarrierIds.includes(carrier.id) ? 'Cheapest' : expressCarrierIds.includes(carrier.id) ? 'Fastest' : '-',
    }));
};
